import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useDocument } from '@/context/DocumentContext';
import { countLength } from '@/helpers/helperFunctions';
import backendApis from '@/services/backendApis';
import {DownloadCloudIcon, Minus, Plus, X, ZoomIn} from 'lucide-react';
import React, { ChangeEvent, useEffect, useRef, useState } from 'react';
import { TransformComponent, TransformWrapper } from 'react-zoom-pan-pinch';
import { toast } from 'sonner';
import services from "@/services/index.js";

type DocumentPreviewProps = {
  documentId: string;
};

const DocumentPreview = (props: DocumentPreviewProps) => {
  const { documentId } = props;
  const { setDocumentId } = useDocument();
  const [docMetadata, setDocMetadata] = useState(null);
  const [pages, setPages] = useState<any>({});
  const [loading, setLoading] = useState(false);

  const [selectedPage, setSelectedPage] = useState(0);
  const [scale, setScale] = useState('1.00');
  const containerRef = useRef<HTMLDivElement>(null);

  const totalPages = docMetadata?.page_count;
  const filename = docMetadata?.original_filename;

  const fetchPages = async (startPage: number, endPage: number) => {
    if (!docMetadata) {
      return;
    }
    try {
      setLoading(true);
      const pageNumbers = Array.from(
        { length: endPage - startPage + 1 },
        (_, i) => i + startPage,
      );
      const params = {
        document_id: documentId,
        page_numbers: pageNumbers,
      };
      const res = await backendApis.get_document_pages(params);
      const page_urls = res.data.page_urls;
      return page_urls.reduce(
        (
          acc: {
            [key: number]: string;
          },
          pageData: string,
          index: number,
        ) => {
          acc[startPage + index] = pageData;
          return acc;
        },
        {},
      );
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const getAllDocPages = async () => {
    if (!docMetadata) {
      return;
    }
    const startPage = 0;
    const endPage = docMetadata.page_count - 1;
    const newPages = await fetchPages(startPage, endPage);
    setPages(newPages);
  };

  const getDocumentMetadata = async () => {
    try {
      const params = {
        document_ids: [documentId],
      };
      const res = await backendApis.get_document_metadata(params);
      const pageCount = Object.values(res.data)[0].page_count;
      setDocMetadata(Object.values(res.data)[0]);
      if (pageCount === 0) {
        toast.error('Page not ingested yet');
      }
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    if (documentId) {
      getDocumentMetadata();
    }
  }, [documentId]);

  useEffect(() => {
    if (docMetadata) {
      getAllDocPages();
    }
  }, [docMetadata]);

  const handlePageChange = (e: ChangeEvent<HTMLInputElement>) => {
    const newPage = Number.parseInt(e.target.value);
    setSelectedPage(newPage - 1);
  };

  const cleanUp = () => {
    setDocMetadata(null);
    setPages({});
  };

  const downloadDocument = async () => {
    try {
      if(!documentId) {
        throw new Error('Document ID not found');
      }
      toast.info('Starting document download');
      const downloadFilename = filename || 'document.pdf';
      const res = await services.backendApis.getDocumentDownloadUrl({
        document_path: `documents/${documentId}/original`
      });
      if (res.err) {
        throw new Error(res.err);
      }
      const exportUrl = res.data;
      const response = await fetch(exportUrl);
      if (!response.ok) {
        throw new Error('Network response was not ok');
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = downloadFilename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Document download complete');
    } catch (e) {
      console.log(e);
      toast.error("Failed to get export URL", {
        description: e.message
      });
    }
  }

  useEffect(() => {
    return () => {
      cleanUp();
    };
  }, []);

  if (!documentId) {
    return <div>Document ID is required</div>;
  }

  if (loading || countLength(pages) === 0) {
    return (
      <div className="bg-cultured min-h-full space-y-3 p-5 md:p-9">
        <div className="flex items-center justify-between gap-2">
          <Skeleton className="h-4 w-1/6 bg-gray-500" />
          <Skeleton className="h-4 w-1/3 bg-gray-500" />
          <Skeleton className="h-4 w-1/4 bg-gray-500" />
        </div>
        <Skeleton className="h-12 w-1/2 bg-gray-500" />
        <Skeleton className="h-24 w-full bg-gray-500" />
        <Skeleton className="h-12 w-1/2 bg-gray-500" />
        <Skeleton className="h-12 w-1/2 bg-gray-500" />
      </div>
    );
  }

  const fetchedPages = Object.keys(pages).map((pageNumber) =>
    Number.parseInt(pageNumber),
  );

  const scalePercentage = Math.round(scale * 100);

  return (
    <div>
      <div className={`bg-cultured flex-1 p-4`}>
        <div className="mx-auto flex w-full flex-col items-center justify-center gap-2 px-1 sm:px-3 md:px-6">
          <TransformWrapper
            wheel={{
              disabled: true,
            }}
            onTransformed={(e) => {
              setScale(Number(e.instance.transformState.scale).toFixed(2));
            }}
            // options={{
            //   limitToBounds: false,
            //   minScale: 0.2,
            //   maxScale: 5,
            // }}
          >
            {({ resetTransform, zoomIn, zoomOut }) => (
              <div className="min-w-full space-y-1">
                <div className="flex items-center gap-4">
                  <div className='flex w-full flex-row items-center justify-between gap-2 md:gap-3'>

                    <div
                      className='flex gap-4'
                    >
                      <div className="flex items-center justify-center space-x-1 text-xs">
                        <span>
                          <Input
                            className="inline-block h-6 max-w-[35px] bg-white px-1 py-1 text-xs"
                            disabled
                            max={docMetadata.page_count}
                            onChange={handlePageChange}
                            type="number"
                            value={selectedPage + 1}
                          />
                        </span>
                        <span>{'/'}</span>
                        <span>{docMetadata.page_count}</span>
                      </div>

                      <div>
                        <Button
                          onClick={() => downloadDocument()}
                          variant='outline'
                          size='sm'
                        >
                          <DownloadCloudIcon size={14} />
                        </Button>
                      </div>
                    </div>
                  </div>

                  <div className="md:text-md text-sm font-bold">
                    <Tooltip>
                      <TooltipTrigger className='max-w-[200px] truncate'>
                        <span className="md:text-md">
                          {filename}
                        </span>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p className='max-w-md p-1 bg-gray-100 rounded-md'>
                          {filename}
                        </p>
                      </TooltipContent>
                    </Tooltip>
                  </div>

                  <div className="flex items-center justify-center space-x-1 p-2">
                    <Button
                      aria-label="Zoom in"
                      className="h-8 w-8 p-1"
                      onClick={() => zoomIn()}
                      size="sm"
                      variant="ghost"
                    >
                      <Plus className="h-4 w-4" />
                    </Button>

                    <div className="relative w-[70px]">
                      <Input
                        className="h-8 bg-white text-center text-sm font-medium"
                        disabled
                        min={0}
                        step={1}
                        type="number"
                        value={scalePercentage}
                      />
                    </div>

                    <Button
                      aria-label="Zoom out"
                      className="h-8 w-8 p-1"
                      onClick={() => zoomOut()}
                      size="sm"
                      variant="ghost"
                    >
                      <Minus className="h-4 w-4" />
                    </Button>

                    <Button
                      aria-label="Reset zoom"
                      className="h-8 w-8 p-1"
                      onClick={() => resetTransform()}
                      size="sm"
                      variant="ghost"
                    >
                      <ZoomIn className="h-4 w-4" />
                    </Button>

                    <Button
                      aria-label="Close"
                      className="h-8 w-8 p-1"
                      onClick={() => setDocumentId(null)}
                      size="sm"
                      variant="ghost"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                </div>

                <TransformComponent
                  wrapperClass={
                    'mx-auto min-w-full flex items-center justify-center'
                  }
                >
                  <div
                    className="top-0 m-0 flex flex-col overflow-y-auto p-0 py-4"
                    ref={containerRef}
                    style={{
                      height: 'calc(100vh - 8rem)',
                    }}
                  >
                    {fetchedPages.map((pageNumber) => (
                      <div
                        className="relative"
                        id={`page-${pageNumber}`}
                        key={pageNumber}
                      >
                        <img
                          alt={
                            docMetadata.original_filename +
                            ` - Page ${pageNumber + 1}`
                          }
                          className="mx-auto w-full"
                          data-page-number={pageNumber}
                          src={pages[pageNumber]}
                        />
                      </div>
                    ))}
                  </div>
                </TransformComponent>
              </div>
            )}
          </TransformWrapper>
        </div>
      </div>
    </div>
  );
};

export default DocumentPreview;
